import React from "react";
import { useNavigate } from "react-router-dom";
import styles from "./CustomerPageLayout.module.scss";
import { BackIcon } from "../../../assets/icons";

// Set by SettingsLayout: true only on top-level settings pages (which have a sidebar entry),
// so they hide the redundant desktop back button. Nested sub-pages get false and keep it.
// Mobile always keeps the back button, where the menu list is hidden behind the detail.
export const InSettingsContext = React.createContext(false);

interface CustomerPageLayoutProps {
	title?: string;
	subtitle?: string;
	children: React.ReactNode;
	onBack?: () => void;
	showBackButton?: boolean;
	className?: string;
	containerClass?: string;
	contentContainerClass?: string;
	icon?: React.ReactNode;
	headerAction?: React.ReactNode; //add btn, wallet badge...

	contentMaxWidth?: string | number;
	isScrollable?: boolean;
	decreaseMainContentPadding?: boolean;
}

const CustomerPageLayout: React.FC<CustomerPageLayoutProps> = ({
	title,
	subtitle,
	children,
	onBack,
	showBackButton = true,
	className = "",
	containerClass = "",
	contentContainerClass = "",
	icon,
	headerAction,

	contentMaxWidth,
	isScrollable = false,
	decreaseMainContentPadding = false,
}) => {
	const navigate = useNavigate();
	const inSettings = React.useContext(InSettingsContext);

	// On the web, settings is a master–detail layout (sidebar + content), so the desktop
	// back button is redundant — navigation happens via the sidebar. Keep it on mobile.
	const showDesktopBackButton = showBackButton && !inSettings;

	const handleBack = () => {
		if (onBack) {
			onBack();
		} else {
			navigate(-1);
		}
	};
	// console.log(`${styles.container} ${containerClass} ${decreaseMainContentPadding !== undefined && decreaseMainContentPadding ? styles.decreasePadding : ""}`);
	return (
		<div className={`${styles.pageLayout} ${isScrollable ? styles.scrollable : ""} ${className}`}>
			{!showBackButton && !title && <div className={styles.mobileTopSpacer} />}
			{/* Mobile Header — the desktop header is hidden below $breakpoint-lg, so this has to
			    render for the title alone too, not only when there's a back button. */}
			{(showBackButton || title) && (
				<div className={styles.mobileHeader}>
					<div className={styles.buttonRow}>
						{showBackButton && (
							<button className={styles.backButton} onClick={handleBack} aria-label="Go back">
								{icon ?? (
									<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M14.6667 5.5L9.16667 11L14.6667 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								)}
							</button>
						)}
						<div className={styles.mobileTitleGroup}>
							{title && <h1 className={styles.mobileTitle}>{title}</h1>}
							{subtitle && <p className={styles.mobileSubtitle}>{subtitle}</p>}
						</div>
					</div>
					{headerAction && <div className={styles.headerAction}>{headerAction}</div>}
				</div>
			)}

			<div className={`${styles.container} ${containerClass} ${decreaseMainContentPadding ? styles.decreasePadding : ""}`}>
				<div
					className={styles.innerWrapper}
					style={contentMaxWidth !== undefined ? { maxWidth: contentMaxWidth } : undefined}
				>
					{/* Desktop Header */}
					<div className={styles.desktopHeader}>
						{title && (
							<div className={styles.titleSection}>
								{showDesktopBackButton ? (
									<button className={styles.backButton} onClick={handleBack}>
										{icon ?? (
											<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M14.6667 5.5L9.16667 11L14.6667 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										)}
									</button>
								) : (
									<></>
								)}
								<div className={styles.titleGroup}>
									<h1 className={styles.title}>{title}</h1>
									{subtitle && <p className={styles.subtitle}>{subtitle}</p>}
								</div>
							</div>
						)}
						{headerAction && <div className={styles.headerAction}>{headerAction}</div>}
					</div>

					{/* Main Content */}
					<div className={`${styles.content} ${contentContainerClass}`}>
						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CustomerPageLayout;
